/**
 * 成就数据
 */

import type { PlayerState } from '../engine/Player';
import { getAlchemyLevel } from '../engine/AlchemySystem';
import { getForgeLevel } from '../engine/ForgeSystem';
import { getTotalOpened, TOTAL_POINTS } from './meridians';
import { getMasteredCount } from '../engine/MeridianSystem';

export interface Achievement {
  id: string;
  name: string;
  desc: string;
  icon: string;
  /** 检查是否达成 */
  check: (p: PlayerState, extra?: any) => boolean;
  /** 奖励描述 */
  rewardDesc: string;
  /** 应用奖励 */
  applyReward: (p: PlayerState) => void;
}

export const ACHIEVEMENTS: Achievement[] = [
  // ===== 修炼 =====
  {
    id: 'reach_foundation',
    name: '初入仙途',
    desc: '突破至筑基期',
    icon: '🌟',
    check: (p) => p.realmKey.startsWith('foundation'),
    rewardDesc: '灵石×200',
    applyReward: (p) => { p.spiritStones += 200; },
  },
  {
    id: 'reach_golden_core',
    name: '金丹大成',
    desc: '突破至金丹期',
    icon: '💫',
    check: (p) => p.realmKey.startsWith('golden_core'),
    rewardDesc: '灵石×500 + 悟道丹×3',
    applyReward: (p) => { p.spiritStones += 500; },
  },
  {
    id: 'reach_nascent_soul',
    name: '元婴老祖',
    desc: '突破至元婴期',
    icon: '👶',
    check: (p) => p.realmKey.startsWith('nascent_soul'),
    rewardDesc: '灵石×2000 + 攻击永久+10',
    applyReward: (p) => { p.spiritStones += 2000; p.attack += 10; },
  },
  {
    id: 'reach_divinity',
    name: '化神之境',
    desc: '突破至化神期',
    icon: '✨',
    check: (p) => p.realmKey.startsWith('divinity'),
    rewardDesc: 'HP上限永久+100',
    applyReward: (p) => { p.maxHp += 100; p.hp += 100; },
  },

  // ===== 战斗 =====
  {
    id: 'kill_50',
    name: '初露锋芒',
    desc: '累计击杀50个敌人',
    icon: '⚔️',
    check: (p) => p.killCount >= 50,
    rewardDesc: '攻击+3',
    applyReward: (p) => { p.attack += 3; },
  },
  {
    id: 'kill_200',
    name: '百战余生',
    desc: '累计击杀200个敌人',
    icon: '🗡️',
    check: (p) => p.killCount >= 200,
    rewardDesc: '攻击+8',
    applyReward: (p) => { p.attack += 8; },
  },
  {
    id: 'kill_1000',
    name: '修罗降世',
    desc: '累计击杀1000个敌人',
    icon: '💀',
    check: (p) => p.killCount >= 1000,
    rewardDesc: '攻击+25',
    applyReward: (p) => { p.attack += 25; },
  },

  // ===== 死亡 =====
  {
    id: 'die_10',
    name: '大难不死',
    desc: '累计死亡10次',
    icon: '💔',
    check: (p) => p.deathCount >= 10,
    rewardDesc: '防御+5',
    applyReward: (p) => { p.defense += 5; },
  },

  // ===== 炼丹/炼器 =====
  {
    id: 'alchemy_10',
    name: '丹道入门',
    desc: '炼丹熟练度达到10级',
    icon: '🧪',
    check: (p) => getAlchemyLevel(p.alchemyExp ?? 0) >= 10,
    rewardDesc: '炼丹成功率永久+3%',
    applyReward: (_p) => {},
  },
  {
    id: 'forge_10',
    name: '炼器入门',
    desc: '炼器熟练度达到10级',
    icon: '🔨',
    check: (p) => getForgeLevel(p.forgeExp ?? 0) >= 10,
    rewardDesc: '炼器成功率永久+3%',
    applyReward: (_p) => {},
  },

  // ===== 灵石 =====
  {
    id: 'rich_1000',
    name: '小富即安',
    desc: '持有灵石达到1000',
    icon: '💰',
    check: (p) => p.spiritStones >= 1000,
    rewardDesc: '坊市购买永久9折',
    applyReward: (_p) => {},
  },

  // ===== 特殊身份 =====
  {
    id: 'escape_captivity',
    name: '炉鼎逃生',
    desc: '以被囚身份成功逃脱',
    icon: '🔓',
    check: (p) => p.identityId === 'imprisoned' && p.escapeBuffRemaining > 0,
    rewardDesc: '灵石×300',
    applyReward: (p) => { p.spiritStones += 300; },
  },
  {
    id: 'rogue_to_golden',
    name: '散修逆袭',
    desc: '散修身份突破至金丹期',
    icon: '🔥',
    check: (p) => p.identityId === 'rogue' && p.realmKey.startsWith('golden_core'),
    rewardDesc: 'HP上限+50 + 攻击+5',
    applyReward: (p) => { p.maxHp += 50; p.hp += 50; p.attack += 5; },
  },
  {
    id: 'master_disciple',
    name: '名师高徒',
    desc: '以前辈弟子身份开始游戏',
    icon: '👴',
    check: (p) => p.identityId === 'master_disciple',
    rewardDesc: '悟道丹×2',
    applyReward: (p) => {},
  },

  // ===== 收集 =====
  {
    id: 'full_equipment',
    name: '全副武装',
    desc: '同时装备武器、防具、饰品、储物',
    icon: '🛡️',
    check: (p) => ['weapon','armor','accessory','storage'].every((s) => (p.equipment as any)[s]),
    rewardDesc: '防御+3',
    applyReward: (p) => { p.defense += 3; },
  },

  // ===== 经脉 =====
  {
    id: 'meridian_10',
    name: '初窥经脉',
    desc: '累计冲开10个穴位',
    icon: '🔮',
    check: (p) => getTotalOpened(p.meridians ?? {}) >= 10,
    rewardDesc: '灵石×500',
    applyReward: (p) => { p.spiritStones += 500; },
  },
  {
    id: 'meridian_24',
    name: '经脉小成',
    desc: '累计冲开24个穴位（半数）',
    icon: '✨',
    check: (p) => getTotalOpened(p.meridians ?? {}) >= 24,
    rewardDesc: '攻击+10',
    applyReward: (p) => { p.attack += 10; },
  },
  {
    id: 'meridian_48',
    name: '八脉全通',
    desc: '奇经八脉48穴全部贯通',
    icon: '🌟',
    check: (p) => getTotalOpened(p.meridians ?? {}) >= TOTAL_POINTS,
    rewardDesc: '全属性+20',
    applyReward: (p) => { p.attack += 20; p.defense += 20; p.maxHp += 100; p.hp += 100; },
  },
  {
    id: 'mastery_first',
    name: '贯通任督',
    desc: '任脉或督脉全部贯通',
    icon: '🔴',
    check: (p) => getMasteredCount(p) >= 1,
    rewardDesc: '防御+5',
    applyReward: (p) => { p.defense += 5; },
  },
  {
    id: 'mastery_four',
    name: '四脉贯通',
    desc: '四条经脉全部贯通',
    icon: '💫',
    check: (p) => getMasteredCount(p) >= 4,
    rewardDesc: '修炼速度永久+5%',
    applyReward: (_p) => {},
  },
  {
    id: 'mastery_all',
    name: '天人合一',
    desc: '奇经八脉全部贯通',
    icon: '👑',
    check: (p) => getMasteredCount(p) >= 8,
    rewardDesc: 'HP上限+200',
    applyReward: (p) => { p.maxHp += 200; p.hp += 200; },
  },
];
